import { get } from 'lodash';
import { MetricDisplayType } from 'modules/graphql';
import { TFunction } from 'react-i18next';
import { localizeCurrency, localizeNumber, localizePercent } from 'utils/l10n';
import { getTranslations } from 'utils/translations';
import { MetricIdentifier } from '../constants';
import {
  BasePageExperimentVariantProperties,
  ExperimentAnalyticsTranslation,
  ExperimentHoldoutBreakdownDetail,
  ExperimentVariantBreakdownDetail,
} from '../types';

type RetrieveMetricResultArgs = {
  dataStore: ExperimentVariantBreakdownDetail | ExperimentHoldoutBreakdownDetail;
  metricMap: Record<string, MetricIdentifier>;
  metric: string;
};

type CalculateRatePercentageArgs = {
  count: number;
  total: number;
};

export const getExperimentsTranslations = getTranslations('experiments');

export const JSXToText = (text: string | JSX.Element) =>
  typeof text === 'string' ? text : text.props.children;

export const italicizedTFunction = (t: TFunction) => (key: string) => <i>{t(key)}</i>;

export const tFunctionWithSource =
  (source: ExperimentAnalyticsTranslation) =>
  (key: string): string =>
    get(source, key) || key;

export const getNonControlVariationCount = (variations: BasePageExperimentVariantProperties[]) =>
  variations.filter(({ control }) => !control).length;

export const getWinnerVariant = (variations: BasePageExperimentVariantProperties[]) => {
  const winnerVariant = variations.find(({ winner }) => {
    return winner === true;
  });
  return winnerVariant;
};

export const getControlVariant = (variations: BasePageExperimentVariantProperties[]) => {
  const controlVariant = variations.find(({ control }) => {
    return control === true;
  });
  return controlVariant;
};

export const printUniqueMetricName = (prefix: string, metric: string, suffix = '') =>
  `${prefix} ${metric} ${suffix}`.trim();

export const retrieveMetricResult = ({
  dataStore,
  metricMap,
  metric,
}: RetrieveMetricResultArgs) => {
  const metricSource = metricMap[metric] || MetricIdentifier.SYSTEM_METRICS;
  return dataStore[metricSource]?.[metric] || {};
};

export const stylishMetricResult = (
  metricResult: number | string,
  displayValue: MetricDisplayType,
): string => {
  if (typeof metricResult === 'string') {
    return metricResult;
  }

  switch (displayValue) {
    case 'currency':
      return localizeCurrency(metricResult);

    case 'percentage':
      return localizePercent(metricResult);

    case 'number':
      return localizeNumber(metricResult);

    default:
      return metricResult.toString();
  }
};

const ratePercentageCalculator = ({
  count,
  total,
  withFallback,
}: CalculateRatePercentageArgs & { withFallback: boolean }) => {
  let rate = count / total;
  /** Returns a finite value. Fallbacks to 0 */
  if (withFallback) {
    rate = count && total && !Number.isNaN(rate) && Number.isFinite(rate) ? rate : 0;
  }

  return {
    rate,
    percentage: localizePercent(rate),
  };
};

export const calculateRatePercentage = (args: CalculateRatePercentageArgs) =>
  ratePercentageCalculator({
    ...args,
    withFallback: false,
  });

export const calculateSafeRatePercentage = (args: CalculateRatePercentageArgs) =>
  ratePercentageCalculator({
    ...args,
    withFallback: true,
  });

export const suffixNumberDisplay = (num: number) => {
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const item = lookup
    .slice()
    .reverse()
    .find((entry) => num >= entry.value);
  let digits = 2;
  if (num > 10000) {
    digits = 0;
  }
  return item ? (num / item.value).toFixed(digits).replace(rx, '$1') + item.symbol : '0';
};
